import { List, Map, Set } from 'immutable';
import Big from 'big.js';

import { getCategoryName, getCategoryPath } from '../taxonomy/categories';
import { isValidNumber, toFixed, toString } from '../number';
import { getCategories, getName } from '../taxonomy';
import { getDateFromKey } from '../time';
import { tagsToString } from '../tags';
import { toLowerCase } from '../string';
import { getSize } from '../iterable/getSize';
import { keepOld } from '../iterable/keepOld';
import { convert } from './convert';
import { reduce } from '../iterable/reduce';
import { get } from '../iterable/get';

import { ABOUT_LIST, CURRENCIES, ENABLED, ERROR, NEW, PRICE_ATTRIBUTE_CURRENCY_TO_API_KEY_MAP, REQUIRED, SELLING_METHOD, SIZE_UNIT, VALUE, WEIGHT_UNIT } from '../../constants/product';
import { EMPTY, SEPARATOR } from '../../constants';
import { ETSY, FAIRE } from '../../constants/channels';
import { ATTRIBUTE } from '../../constants/attributes';
import { SECOND } from '../../constants/time';
import { FORM } from '../../constants/forms';

export function makeValue(value) {
  return Map({ [VALUE]: value });
}

export function shapeEbayDetailsForApp(details) {
  function reduceSpecifics(result, detail) {
    const name = get('name')(detail);
    const value = get('value')(detail);

    return name && getSize(value) > 0
      ? result.set(name, Map({ name, value: Set(value) }))
      : result;
  }

  return details.reduce(reduceSpecifics, EMPTY.MAP);
}

export function shapeEbayDetailsForAPI({ details, toImmutable = true, toList = true }) {
  function reduceDetails(result, specific, key) {
    const name = specific.get('name');
    const value = specific.get('value');

    if (!name || getSize(value) === 0 || details.hasIn([ERROR, key])) {
      return result;
    }

    if (toImmutable) {
      return toList
        ? result.push(Map({ name, value: value.toList() }))
        : result.set(name, Map({ value: value.toList() }));
    }

    if (toList) {
      result.push({ name, value: value.toArray() });
    } else {
      result[name] = { value: value.toArray() };
    }

    return result;
  }

  return details
    .get(VALUE, EMPTY.MAP)
    .reduce(
      reduceDetails,
      toImmutable
        ? toList
          ? EMPTY.LIST
          : EMPTY.MAP
        : toList
          ? []
          : {}
    );
}

export function shapePersonalizationForApp(data) {
  const [
    personalizationEnabled,
    personalizationRequired,
    personalizationInstructions,
    personalizationCharCountMax,
  ] = get(
    'is_personalizable',
    'personalization_is_required',
    'personalization_instructions',
    'personalization_char_count_max',
  )(data);

  const enabled = typeof personalizationEnabled === 'boolean' ? personalizationEnabled : false;
  const required = typeof personalizationRequired === 'boolean' ? personalizationRequired : false;
  const instructions = personalizationInstructions || '';
  const charCountMax = isValidNumber(personalizationCharCountMax) ? personalizationCharCountMax : null;
  return Map({
    [ENABLED]: enabled,
    [ATTRIBUTE.CHAR_COUNT_MAX]: Map({ [VALUE]: charCountMax }),
    [ATTRIBUTE.INSTRUCTIONS]: Map({ [VALUE]: instructions }),
    [REQUIRED]: required,
  });
}

export function shapeTaxonomyAttributesForApp(attributes) {
  function parse(string) {
    return parseInt(string) || -1;
  }

  function reduceAttributes(result, attribute) {
    const propertyId = parseInt(attribute.propertyId);

    return isNaN(propertyId)
      ? result
      : result.set(propertyId, Map({
        scaleId: parseInt(attribute.scaleId) || null,
        values: List(attribute.values || []),
        valueIds: List((attribute.valueIds || []).map(parse)),
      }));
  }

  return attributes.reduce(reduceAttributes, EMPTY.MAP);
}

export function shapeTaxonomyAttributesForAPI({ taxonomyAttributes, toImmutable = true }) {
  function reduceTaxonomyAttributes(result, value, propertyId) {
    if (!isValidNumber(propertyId) || taxonomyAttributes.hasIn([ERROR, propertyId])) return result;

    const scaleId = value.get('scaleId', null);
    const values = value.get('values', EMPTY.LIST);
    const valueIds = value.get('valueIds', EMPTY.LIST);
    const attribute = {
      scaleId,
      values: toImmutable ? values : values.toArray(),
      valueIds: toImmutable ? valueIds : valueIds.toArray(),
    };

    if (toImmutable) {
      return result.set(propertyId, Map(attribute));
    }

    result[propertyId] = attribute;
    return result;
  }

  return taxonomyAttributes.reduce(reduceTaxonomyAttributes, toImmutable ? EMPTY.MAP : {});
}

export function getPriceFromCents(cents) {
  if (!isValidNumber(cents)) return '';

  return new Big(cents).div(100).toFixed(2, 0);
}

export function shapeUnixDate(timestamp) {
  return isValidNumber(timestamp)
    ? new Date(timestamp * SECOND).toISOString()
    : timestamp;
}

export function shapeKeyDate(key) {
  return key
    ? getDateFromKey(key)
    : null;
}

export function shapeAboutValue({ attribute, channel, value }) {
  return ABOUT_LIST[channel][attribute]?.includes(value)
    ? value
    : null;
}

export function shapeFilesForApp(files = []) {
  function mapFiles(file) {
    const {
      create_timestamp: created,
      filename,
      filesize,
      file_url: url,
      listing_file_id: fileId,
      name,
      size,
    } = file;

    return Map({
      created: shapeUnixDate(created),
      file_url: url,
      listing_file_id: fileId,
      name: filename || name,
      size: filesize || size,
    });
  }

  return List(files.map(mapFiles));
}

export function shapeEbayShippingAttributeForApp({ attribute, product }) {
  const unit = get(`shipping_package_${attribute}_unit`)(product);
  const value = get(`shipping_package_${attribute}`)(product);

  return Map({ [ATTRIBUTE.UNIT]: unit, [VALUE]: toString(value) || '' });
}

export function shapeFairePricesForApp({ attribute, product, unset = '' }) {
  function reduceCurrencies(result, currency) {
    const price = get(PRICE_ATTRIBUTE_CURRENCY_TO_API_KEY_MAP[attribute][currency])(product);
    return result.set(currency, toFixed(price, 2) || unset);
  }

  return CURRENCIES[FAIRE].reduce(reduceCurrencies, EMPTY.MAP);
}

export function shapeFairePricesForApi({ inventory, toImmutable = true }) {
  let result = toImmutable ? EMPTY.MAP : {};

  for (const attribute of [ATTRIBUTE.RETAIL_PRICE, ATTRIBUTE.WHOLESALE_PRICE]) {
    for (const [currency, value] of inventory.getIn([attribute, VALUE]).entries()) {
      const key = PRICE_ATTRIBUTE_CURRENCY_TO_API_KEY_MAP[attribute][currency];

      if (toImmutable) {
        result = result.set(key, Map({ value: value || null }));
      } else {
        result[key] = { value: value || null };
      }
    }
  }

  return result;
}

export function shapeFaireShippingAttributeForApp({ attribute, product, unset = '' }) {
  return toString(get(`measurements_${attribute}`)(product)) || unset;
}

export function shapeFaireMassUnitForApp(value) {
  switch (value) {
    case 'GRAMS': {
      return WEIGHT_UNIT.G;
    }

    case 'KILOGRAMS': {
      return WEIGHT_UNIT.KG;
    }

    case 'OUNCES': {
      return WEIGHT_UNIT.OZ;
    }

    case 'POUNDS': {
      return WEIGHT_UNIT.LB;
    }

    default: {
      return WEIGHT_UNIT.LB;
    }
  }
}

export function shapeFaireDistanceUnitForApp(value) {
  switch (value) {
    case 'CENTIMETERS': {
      return SIZE_UNIT.CM;
    }

    case 'FEET': {
      return SIZE_UNIT.FT;
    }

    case 'INCHES': {
      return SIZE_UNIT.IN;
    }

    case 'METERS': {
      return SIZE_UNIT.M;
    }

    case 'MILLIMETERS': {
      return SIZE_UNIT.MM;
    }

    case 'YARDS': {
      return SIZE_UNIT.YD;
    }

    default: {
      return SIZE_UNIT.IN;
    }
  }
}

export function shapeFaireMassUnitForApi(value) {
  switch (value) {
    case WEIGHT_UNIT.G: {
      return 'GRAMS';
    }

    case WEIGHT_UNIT.KG: {
      return 'KILOGRAMS';
    }

    case WEIGHT_UNIT.OZ: {
      return 'OUNCES';
    }

    case WEIGHT_UNIT.LB: {
      return 'POUNDS';
    }

    default: {
      return 'POUNDS';
    }
  }
}

export function shapeFaireDistanceUnitForApi(value) {
  switch (value) {
    case SIZE_UNIT.CM: {
      return 'CENTIMETERS';
    }

    case SIZE_UNIT.FT: {
      return 'FEET';
    }

    case SIZE_UNIT.IN: {
      return 'INCHES';
    }

    case SIZE_UNIT.M: {
      return 'METERS';
    }

    case SIZE_UNIT.MM: {
      return 'MILLIMETERS';
    }

    case SIZE_UNIT.YD: {
      return 'YARDS';
    }

    default: {
      return 'INCHES';
    }
  }
}

export function getFaireShipping(product) {
  const [
    minimumOrderQuantity,
    perStyleMinimumOrderQuantity,
    unitMultiplier,
  ] = get(
    'minimum_order_quantity',
    'per_style_minimum_order_quantity',
    'unit_multiplier',
  )(product);

  function reduceFaireOptions(result, option) {
    return result || toLowerCase(get('name')(option)) === 'size';
  }

  const sellingMethod = (
    isValidNumber(perStyleMinimumOrderQuantity) &&
    perStyleMinimumOrderQuantity > 0 &&
    reduce(get(['variations', 'options'])(product), false, reduceFaireOptions)
  )
    ? SELLING_METHOD.SIZE
    : isValidNumber(unitMultiplier) && unitMultiplier > 1
      ? SELLING_METHOD.CASE
      : SELLING_METHOD.ITEM;

  return Map({
    [ATTRIBUTE.CASE_SIZE]: makeValue(
      sellingMethod === SELLING_METHOD.CASE
        ? toString(unitMultiplier)
        : ''
    ),
    [ATTRIBUTE.MINIMUM_ORDER_QUANTITY]: makeValue(
      sellingMethod === SELLING_METHOD.CASE || sellingMethod === SELLING_METHOD.ITEM
        ? toString(minimumOrderQuantity)
        : ''
    ),
    [ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY]: makeValue(
      sellingMethod === SELLING_METHOD.SIZE
        ? toString(perStyleMinimumOrderQuantity)
        : ''
    ),
    [ATTRIBUTE.SELLING_METHOD]: makeValue(sellingMethod),
  });
}

export function shapeKeywordsForApp({ value = {}} = {}) {
  const { focus_keyword: focusKeyword = '', all_keywords: allKeywords = [] } = value;
  return Map({ all: List(allKeywords), focus: focusKeyword });
}

export function shapeKeywordsForAPI({ value = EMPTY.MAP } = {}) {
  return {
    all_keywords: value.get('all', EMPTY.LIST).toArray(),
    focus_keyword: value.get('focus', ''),
  };
}

export function shapeAltTextForAPI({ product }) {
  return product.getIn([FORM.PHOTOS, VALUE, 0, 'alt_text']) || '';
}

export function shapeCategoriesForAPI({ channel, product }) {
  function mapCategories(categoryId) {
    return getName({ category: categoryId, channel });
  }

  return getCategories({ category: product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]), channel })
    .map(mapCategories)
    .join(SEPARATOR.COMMA_SPACE);
}

export function shapeEbayCategoriesForAPI({ product, taxonomyData }) {
  function mapCategories(category) {
    return getCategoryName({ category, taxonomyData });
  }

  return getCategoryPath({ category: product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]), taxonomyData })
    .map(mapCategories)
    .join(SEPARATOR.COMMA_SPACE);
}

export function shapeEbayItemSpecificsForAPI({ product }) {
  function reduceSpecifics(result, detail) {
    const name = get('name')(detail);
    const value = get('value')(detail);
    return name && getSize(value) > 0
      ? result.push(`${name}: ${value.join(SEPARATOR.COMMA_SPACE)}`)
      : result;
  }
  return product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE]).reduce(reduceSpecifics, EMPTY.LIST).join(` ${SEPARATOR.VERTICAL_BAR_SPACE} `);
}

export function shapeDescriptionForAPI({ channel, product }) {
  return convert({
    from: channel,
    to: ETSY,
    type: ATTRIBUTE.DESCRIPTION,
    value: product.getIn([FORM.DESCRIPTION, VALUE]),
  });
}

export function shapeTagsForAPI({ channel, product }) {
  return tagsToString({
    channel,
    separator: SEPARATOR.COMMA_SPACE,
    tags: product
      .getIn([FORM.TAGS, VALUE])
      .mergeWith(keepOld, product.getIn([FORM.TAGS, NEW])),
  });
};

export function shapeFaireVariationsForAPI({ product }) {
  const variants = product.getIn([FORM.VARIATIONS, 'variants']);

  if (!variants || !getSize(variants)) {
    return '';
  }

  const totalResult = [];
  variants.forEach((currentVariant) => {
    const variantOptions = currentVariant.get('options');
    if (getSize(variantOptions)) {
      const localResult = `${currentVariant.get('name')}: ${variantOptions.map(option => option.get('value')).join(SEPARATOR.COMMA_SPACE)}`;
      totalResult.push(localResult);
    }
  });
  return totalResult.join(SEPARATOR.VERTICAL_BAR_SPACE);
};

